/* eslint-disable import/order */
import { LazyIssueApp } from '@jira/issue__lazy-app/src/LazyIssueApp.tsx';
import { MenuIdProjects } from '@jira/navigation-apps__constants/src/MenuIdProjects.tsx';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
} from '@jira/router-resources__classic-projects/src/getRapidboardResources.tsx';
import { LazyAtlassianNavigation } from '@jira/navigation-apps__atlassian-navigation/src/async.tsx';
import { CLASSIC_BOARD } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { AGGRESSIVE_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { genericProjectLayout } from '@jira/platform__layouts-generic-project-layout/src/index.tsx';
import { embeddedIssuePreloadLayoutBuilder } from '@jira/platform__layouts-issue-preload-layout/src/index.tsx';
import { getIssueViewResources } from '@jira/router-resources__issue-view/src/getIssueViewResources.tsx';
import { forgeProjectModuleResource } from '@jira/navigation-apps__resources/src/controllers/forge/index.tsx';
import { horizontalNavJiraResource } from '@jira/navigation-apps__resources/src/controllers/horizontal-navigation/horizontal-nav-jira/index.tsx';
import { staticProjectSidebarConnectAddonsNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar-connect-addons/index.tsx';
import { staticProjectSidebarNavigationResource } from '@jira/navigation-apps__resources/src/controllers/sidebar/static-project-sidebar/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import {
	getNavigationResources,
	getUserBoardNavigationResources,
} from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { projectContextResource } from '@jira/router-resources__project-context/src/projectContextResource.tsx';
import { incidentExperimentTraitResource } from '@jira/router-resources__software-incidents/src/incidentExperimentTraitResource.tsx';
import { legacyRapidBoardRoute } from '@jira/router-routes__software-classic-rapid-board-routes/src/legacyRapidBoardRoute.tsx';

import {
	LazyRapidBoard,
	LazyRapidBoardApps,
	RapidBoardSpaPage,
} from '@jira/router-routes__user-board-entries/src/ui/async.tsx';
import { ClassicBoardSkeleton } from '@jira/platform__skeletons/src/ui/classic-board/ClassicBoardSkeleton.tsx';
import { AsyncHorizontalNavJira } from '@jira/navigation-apps__horizontal-nav-jira/src/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';

export const legacyRapidBoardRouteEntry: Route = createEntry(legacyRapidBoardRoute, () => ({
	group: 'classic-software',
	ufoName: 'classic-board',
	// eslint-disable-next-line no-restricted-syntax
	component: RapidBoardSpaPage,
	skeleton: ClassicBoardSkeleton,
	wrmMode: fg('jira-cmp-board-wrmmode-early') ? 'early' : undefined,

	layout: composeLayouts(
		genericProjectLayout,
		embeddedIssuePreloadLayoutBuilder([
			{ query: ['selectedIssue', 'view=detail'] },
			{ query: ['selectedIssue', 'modal=detail'] },
		]),
	),

	navigation: {
		menuId: MenuIdProjects,
		horizontal: AsyncHorizontalNavJira,
	},

	resources: [
		...getNavigationResources(),
		...getIssueViewResources(),
		// these have been expanded from getNavigationSidebarProjectResource - please examine whether your route requires these resources in nav4 and remove them if needed
		// A guide to check & cleanup: https://hello.atlassian.net/wiki/x/Q8XybwE
		incidentExperimentTraitResource,
		projectContextResource,
		forgeProjectModuleResource,
		staticProjectSidebarNavigationResource,
		staticProjectSidebarConnectAddonsNavigationResource,
		horizontalNavJiraResource,
		// end of resources expanded from getNavigationSidebarProjectResource
		...getRapidboardResources(),
		getRapidBoardDataResource(),
		...getUserBoardNavigationResources(),
	],

	afterPaint: [LazyIssueApp],
	forPaint: [LazyAtlassianNavigation, LazyRapidBoard, LazyRapidBoardApps],

	meta: {
		capability: CLASSIC_BOARD,
	},

	earlyAssets: AGGRESSIVE_EARLY_ASSET_STRATEGY,
	supportsPreviewPanel: true,

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));
